//src/utils/firebase-config.js

import { initializeApp } from "firebase/app";
import { initializeAuth, indexedDBLocalPersistence, browserLocalPersistence, browserPopupRedirectResolver, connectAuthEmulator } from "firebase/auth";
import { connectFirestoreEmulator, initializeFirestore, persistentLocalCache, memoryLocalCache, persistentMultipleTabManager } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getAnalytics, isSupported } from "firebase/analytics";
import logger from './logger';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

for (const [k,v] of Object.entries(firebaseConfig)) {
  if (!v) logger.warn(`[firebase-config] Missing env var: ${k}`);
}

const app = initializeApp(firebaseConfig);
const _isNative = typeof window !== 'undefined' && !!window?.Capacitor?.isNativePlatform?.();
const auth = initializeAuth(app, {
  persistence: _isNative ? indexedDBLocalPersistence : browserLocalPersistence,
  popupRedirectResolver: _isNative ? undefined : browserPopupRedirectResolver,
});


// Initialize Firestore cache:
// - Web: persistentLocalCache (IndexedDB, supports offline reads/writes across tabs)
// - Native (Capacitor WKWebView): memoryLocalCache — avoids IndexedDB locking issues in
//   WKWebView that can stall onSnapshot real-time listeners. The WKWebView instance
//   persists for the lifetime of the app session so memory cache is sufficient.
const db = initializeFirestore(app, {
  localCache: _isNative
    ? memoryLocalCache()
    : persistentLocalCache({ tabManager: persistentMultipleTabManager() })
});
const storage = getStorage(app);
const functions = getFunctions(app);

// Initialize Google Analytics (only in production, not with emulators)
let analytics = null;
if (import.meta.env.VITE_USE_EMULATOR !== 'true') {
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
      logger.debug('📊 Google Analytics initialized');
    } else {
      logger.debug('📊 Google Analytics not supported in this environment');
    }
  }).catch((error) => {
    logger.warn('Analytics initialization failed:', error.message);
  });
}

// Track emulator initialization status
let emulatorsInitialized = false;

// Connect to emulators in development
if (import.meta.env.VITE_USE_EMULATOR === 'true' && import.meta.env.DEV && !emulatorsInitialized) {
  const emulatorHost = import.meta.env.VITE_EMULATOR_HOST || 'localhost';

  try {
    connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true });
    logger.debug('🔗 Connected to Auth Emulator');
  } catch (error) {
    // Ignore "already connected" errors - Firebase SDK handles this gracefully in v12
    if (!error.message?.includes('already')) {
      logger.warn('Auth emulator connection failed:', error.message);
    }
  }

  try {
    connectFirestoreEmulator(db, emulatorHost, parseInt(import.meta.env.VITE_FIRESTORE_PORT || '8080'));
    logger.debug('🔗 Connected to Firestore Emulator');
  } catch (error) {
    if (!error.message?.includes('already')) {
      logger.warn('Firestore emulator connection failed:', error.message);
    }
  }

  try {
    connectStorageEmulator(storage, emulatorHost, parseInt(import.meta.env.VITE_STORAGE_PORT || '9199'));
    logger.debug('🔗 Connected to Storage Emulator');
  } catch (error) {
    if (!error.message?.includes('already')) {
      logger.warn('Storage emulator connection failed:', error.message);
    }
  }

  try {
    connectFunctionsEmulator(functions, emulatorHost, parseInt(import.meta.env.VITE_FUNCTIONS_PORT || '5001'));
    logger.debug('🔗 Connected to Functions Emulator');
  } catch (error) {
    if (!error.message?.includes('already')) {
      logger.warn('Functions emulator connection failed:', error.message);
    }
  }

  // Mark emulators as initialized
  emulatorsInitialized = true;
  logger.debug('✅ Emulator initialization complete');
}

// Firestore collection constants
// Migration completed Mar 2026 - consolidated from 17 to 6 active collections
export const COLLECTIONS = {
  // Core data
  JOURNAL_ENTRIES: 'journalEntries',
  INSIGHTS: 'insights',
  GOALS: 'goals',
  WEEKLY_SUMMARIES: 'weeklySummaries',
  PLANNER_BRIEFINGS: 'plannerBriefings',
  INTENTIONS: 'intentions',

  // User profile (consolidated from profiles + 5 preference collections)
  USER_PROFILES: 'userProfiles',

  // Consolidated collections
  USER_AI_MEMORY: 'userAIMemory',       // Full AI stats (mood, patterns, writing analysis)
  USER_STATS: 'userStats',              // Hot stats only: totalEntries, streak, lastEntryDate
  APP_SESSIONS: 'appSessions',          // App session duration records (Phase 3 data collection)
  USER_LIFE_CONTEXT: 'userLifeContext', // comprehensive AI memory
  USER_OCR_LEARNING: 'userOCRLearning', // from ocrContext + ocrFeedback
  CACHE: '_cache',                      // from sentimentDataCache + themesCache

  // AI signals layer (subcollections of userProfiles/{uid})
  // Usage: collection(db, COLLECTIONS.USER_PROFILES, uid, 'signals')
  SIGNALS: 'signals',
  SUMMARIES: 'summaries',

  // Auth and app data
  NOTIFICATION_PREFERENCES: 'notificationPreferences',
  FCM_TOKENS: 'fcmTokens',
  HEXAGRAMS: 'hexagrams',

  // Shared entries (public read, no auth required)
  SHARED_ENTRIES: 'sharedEntries',
};

export { auth, db, storage, functions, analytics };
